// One document, read from the local store with the unsent writes folded over
// it. `useStoredCollection`'s twin, and its header is the one to read first —
// everything there about the three re-read triggers, the newest-read-wins
// token, the two-argument `then` and "marked, never blocked" applies here
// unchanged.
//
// ── What differs from the collection hook, and why ───────────────────────
//
// **A document scope is not its collection.** `settings/studio` and
// `settings/onboarding` live under one collection name and are refreshed by
// two different listeners, so each gets its own receipt — `documentScope`
// in `appStore.js` is what keeps them apart. A document listener that wrote
// into the *collection* receipt would claim to have refreshed a window it
// never asked for, and `settings/studio` would read `fresh` because
// `settings/onboarding` had arrived.
//
// **An absent document is stored as an answer**, not as a missing row. The
// `subscribe` contract below is `(onDocument) => unsubscribe` where
// `onDocument` takes `data | null`, and the `null` is load-bearing: it becomes
// `exists: false`, which is what lets a reader tell "the server says there is
// nothing here" from "we have never been told". That distinction is failure 1
// of #207's plan — see `useDocumentWindow.js`'s header for what it costs.
//
// **A refused document leaves the scope untouched**, which is
// `replaceDocument`'s asymmetry rather than this file's: dropping the only row
// of a document scope would read back as `exists: false`, a claim we have not
// earned. So a refusal here means the previous answer stands and the read
// stays `stale` — reported through `storeRefusals`, never silently.
//
// ── Imports no Firebase, on purpose ──────────────────────────────────────
//
// As with `useStoredCollection`: `subscribe` and `path` are handed in, so this
// is a state machine the `ui` project can test and that project's guarantee —
// that it cannot reach Firebase at all — is kept.
//
// ── `loading` is this hook's, not the caller's to derive (#279) ──────────
//
// A caller deriving `loading` from `state` misses one case: **`state` is
// `null` before the first read comes back**, and `null` is not `'loading'`.
// `documentIsLoading` (#267) reads `state === 'loading' && !exists`, so on the
// placeholder below it answers *not loading*, and a caller asking "has this
// been read?" is told yes while holding nothing.
//
// `useSettings` would then take `loading: false` between mount and the first
// IndexedDB round trip and serve `normaliseStudio(null)`, the full set of
// defaults (#279). `documentIsLoading` is right about every read the overlay
// returns and wrong only about the placeholder, which never went through the
// overlay, so the question is answered here, where *no read yet* and *a read
// that came back empty* can be told apart.

import { useEffect, useState } from 'react';
import { documentIsLoading, changeTouches } from './overlay';

/** One shared empty list, so `|| []` cannot be a new identity per snapshot. */
const EMPTY_REFUSALS = Object.freeze([]);

/**
 * *This subscription has not dispatched this value yet*, distinct from every
 * value it could dispatch (`useStoredCollection` has the same guard): these
 * guards are the effect's and the state they guard is the component's, so a
 * new subscription must not start from a guess about what the old one left
 * behind.
 */
const UNSET = Symbol('nothing dispatched by this subscription');

/**
 * What a caller sees before the first read has come back.
 *
 * `state: null` is *no read has resolved*, which is neither of `appStore`'s
 * three values and must not be mistaken for one — see the header.
 */
const NOTHING_YET = {
  data: null,
  exists: false,
  state: null,
  seenAt: null,
  pendingWrite: false,
  partial: false,
  refusals: [],
  storeRefusals: [],
  error: null,
  loading: true,
};

/**
 * @param uid         the studio. Falsy means signed out: nothing is read.
 * @param collection  a `COLLECTIONS` name.
 * @param id          the document id.
 * @param subscribe   `(onDocument) => unsubscribe`, where `onDocument` takes
 *                    the document's data or **`null`** for a document the
 *                    snapshot says is not there, and optionally
 *                    `{ fromCache }` — the snapshot's (#682), without which
 *                    the answer is taken as the server's.
 * @param path        `theReadPath()` — `{ store, overlay, onQueueChange }`.
 * @param enabled     the flag. `false` makes this hook inert.
 *
 * Returns `{ data, exists, state, seenAt, pendingWrite, partial, refusals,
 * storeRefusals, error, loading }`. **`exists` is the field to read, not the
 * truthiness of `data`** — see `overlay.readDocument`'s own doc comment for the
 * four answers this keeps apart.
 *
 * `loading` is *there is nothing to show yet* — `documentIsLoading` (#267) with
 * the pre-first-read placeholder counted in (#279). It is **not** "the server
 * has answered": a `stale` receipt and a queued creation both make this false
 * while the server has said nothing this session. A caller that wants the other
 * question reads `state` — see `useDocumentWindow`, which offers both by name.
 */
export function useStoredDocument({
  uid, collection, id, subscribe, path, enabled = true,
}) {
  const [read, setRead] = useState(null);
  // `EMPTY_REFUSALS` rather than a literal, so the first snapshot of a healthy
  // studio matches what is already held and schedules nothing (#379). A fresh
  // `[]` here would make the very first store write a guaranteed re-render for
  // a list that was empty and still is.
  const [storeRefusals, setStoreRefusals] = useState(EMPTY_REFUSALS);
  const [error, setError] = useState(null);
  // The listener's own error (#647) — `useStoredCollection`'s `listenError`,
  // whose comment says why it is not `error`.
  const [listenError, setListenError] = useState(null);

  useEffect(() => {
    setListenError(null);
    if (!enabled || !uid || !path) {
      setRead(null);
      setStoreRefusals(EMPTY_REFUSALS);
      setError(null);
      return undefined;
    }

    let live = true;
    // Newest read wins rather than last to arrive. Same reason as the
    // collection hook: three independent triggers, each a couple of IndexedDB
    // round trips, so two can settle in either order and the screen must not
    // go backwards.
    let latest = 0;

    // What this subscription last handed each setter, so an unchanged value
    // schedules no render — `useStoredCollection`'s identity guard, for the
    // same queue fan-out (#379).
    let lastRead = UNSET;
    let lastError = UNSET;
    let lastStoreRefusals = UNSET;

    const reread = () => {
      const token = (latest += 1);
      // Two-argument `then`: this rejection handler is for the read's own
      // failure and must not become a catch for a bug in the handler beside it.
      path.overlay.readDocument(uid, collection, id).then(
        (next) => {
          if (!live || token !== latest) return;
          if (next !== lastRead) {
            lastRead = next;
            setRead(next);
          }
          // `!== null` catches both an error this subscription reported and
          // `UNSET` — its first clean read, which must clear whatever the
          // previous subscription left in `error`.
          if (lastError !== null) {
            lastError = null;
            setError(null);
          }
        },
        (err) => {
          if (!live || token !== latest) return;
          console.error(`[readStore] reading ${collection}/${id} failed:`, err);
          lastError = err;
          setError(err);
        },
      );
    };

    // The document's own path, not its collection's (#389). A queued write to
    // a *sibling* document cannot move this read: `readDocument` folds the ops
    // at this exact path and nothing else.
    //
    // `changeTouches` tests `startsWith`, so this also matches an id that has
    // this one as a prefix — `…/abc` matches `…/abc123`. That is a spurious
    // wake that costs one read whose answer is discarded by identity, and it
    // keeps one prefix rule beside the collection hook's. Missing a wake is
    // the error this must never make.
    const self = `users/${uid}/${collection}/${id}`;
    const unsubscribeQueue = path.onQueueChange((change) => {
      if (changeTouches(change, self)) reread();
    });
    // `meta.fromCache` (#682): a document the SDK answered from its own cache
    // is stored and stays `stale` until the server's answer lands, as a
    // collection window does.
    const unsubscribeSnapshot = subscribe((data, meta) => {
      path.store.replaceDocument(uid, collection, id, data, {
        fromCache: Boolean(meta && meta.fromCache === true),
      }).then(
        (result) => {
          if (!live) return;
          // `.length ? … : EMPTY_REFUSALS` rather than `|| EMPTY_REFUSALS`:
          // `appStore` hands back its *own* shared empty list, which is truthy.
          // Normalising here makes every empty answer this hook sees the same
          // object, the first included, without either file importing the
          // other's constant.
          const refused = result?.refusals?.length ? result.refusals : EMPTY_REFUSALS;
          if (refused !== lastStoreRefusals) {
            lastStoreRefusals = refused;
            setStoreRefusals(refused);
          }
          reread();
        },
        (err) => {
          if (!live) return;
          console.error(`[readStore] storing ${collection}/${id} failed:`, err);
          lastError = err;
          setError(err);
          // Still re-read: the store is unchanged, so this shows the previous
          // answer with the queue folded over it, which is better than blank.
          reread();
        },
      );
    }, (err) => {
      if (!live) return;
      console.error(`[readStore] listening to ${collection}/${id} failed:`, err);
      setListenError(err);
    });

    // Before the network says anything. The whole point of a read store.
    reread();

    return () => {
      live = false;
      unsubscribeQueue();
      if (typeof unsubscribeSnapshot === 'function') unsubscribeSnapshot();
    };
  }, [uid, collection, id, subscribe, path, enabled]);

  // Inert: not loading, because nothing is being read and nothing is coming.
  // The same answer `useStoredCollection` gives, for the same reason.
  if (!enabled) return { ...NOTHING_YET, loading: false, enabled: false };
  if (read === null) {
    return {
      ...NOTHING_YET, storeRefusals, error, failed: Boolean(listenError || error), enabled: true,
    };
  }
  return {
    data: read.data,
    exists: read.exists,
    state: read.state,
    seenAt: read.seenAt,
    pendingWrite: read.pendingWrite,
    partial: read.partial,
    refusals: read.refusals,
    storeRefusals,
    error,
    loading: documentIsLoading(read),
    failed: Boolean(listenError),
    enabled: true,
  };
}
